import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth-context';
import { LayoutDashboard, MessageCircle, Clock, LogOut, Menu, PanelLeftClose, PanelLeft, Stethoscope, BarChart3, Inbox, Salad } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { Sheet, SheetContent } from '../components/ui/sheet';
import { Badge } from '../components/ui/badge';
import { getInitials } from '../lib/utils';
import { useState, useEffect } from 'react';
import api from '../lib/api';

const navItems = [
  { to: '/doctor/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
  { to: '/doctor/medical-inbox', icon: Salad, label: 'Consultations' },
  { to: '/doctor/inbox', icon: Inbox, label: 'Inbox' },
  { to: '/doctor/queue', icon: Stethoscope, label: 'Patient Queue' },
  { to: '/doctor/consultations', icon: MessageCircle, label: 'Consultations' },
  { to: '/doctor/availability', icon: Clock, label: 'Availability' },
  { to: '/doctor/referrals', icon: BarChart3, label: 'Referrals' },
];

function SidebarContent({ onClose, collapsed }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  // Nutritionists (medical team) only need the Consultations inbox — hide the
  // legacy OPS doctor items (Inbox / Patient Queue / Consultations / Referrals)
  // that confused onboarded nutritionists.
  const isNutritionist = user?.medical_category === 'nutritionist';
  const items = isNutritionist
    ? navItems.filter(i => i.to === '/doctor/medical-inbox')
    : navItems;
  return (
    <div className="flex flex-col h-full">
      <div className="p-4 border-b">
        <div className="flex items-center gap-2">
          <div className="h-10 w-10 shrink-0 rounded-xl overflow-hidden bg-white flex items-center justify-center">
            <img src="/logo.png" alt="Soul Sanskar" className="h-14 w-14 object-contain scale-150" />
          </div>
          {!collapsed && <div><h1 className="font-heading text-base font-semibold leading-tight">Soul Sanskar</h1><p className="text-[10px] text-muted-foreground">Doctor Portal</p></div>}
        </div>
      </div>
      <nav className="flex-1 p-2 space-y-0.5">
        {items.map(item => (
          <NavLink key={item.to} to={item.to} onClick={onClose}
            className={({ isActive }) => `flex items-center gap-3 px-3 py-2 rounded-xl text-sm font-medium transition-colors duration-200 ${isActive ? 'bg-primary/10 text-primary border-l-2 border-primary' : 'text-muted-foreground hover:bg-muted hover:text-foreground'}`}
            title={collapsed ? item.label : undefined}>
            <item.icon className="h-4 w-4 shrink-0" />{!collapsed && item.label}
          </NavLink>
        ))}
      </nav>
      <div className={`p-3 border-t ${collapsed ? 'px-2' : ''}`}>
        {!collapsed && <div className="flex items-center gap-3 mb-3"><Avatar className="h-8 w-8"><AvatarFallback className="bg-primary/10 text-primary text-xs">{getInitials(user?.name)}</AvatarFallback></Avatar><div className="flex-1 min-w-0"><p className="text-sm font-medium truncate">{user?.name}</p><p className="text-xs text-muted-foreground">Doctor</p></div></div>}
        <Button variant="ghost" size="sm" data-testid="sign-out-button" className={`w-full ${collapsed ? 'px-2' : 'justify-start'} text-muted-foreground`} onClick={() => { logout(); navigate('/login'); }}><LogOut className="h-4 w-4 mr-2" />{!collapsed && 'Sign out'}</Button>
      </div>
    </div>
  );
}

export default function DoctorLayout() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  return (
    <div className="flex h-screen bg-background">
      <aside className={`hidden lg:flex ${collapsed ? 'w-[68px]' : 'w-[248px]'} shrink-0 border-r bg-white/70 backdrop-blur flex-col transition-all duration-300 relative`}>
        <SidebarContent onClose={() => {}} collapsed={collapsed} />
        <button onClick={() => setCollapsed(!collapsed)} className="absolute -right-3 top-20 z-10 h-6 w-6 rounded-full border bg-white shadow-sm flex items-center justify-center hover:bg-muted transition-colors">
          {collapsed ? <PanelLeft className="h-3 w-3" /> : <PanelLeftClose className="h-3 w-3" />}
        </button>
      </aside>
      <Sheet open={mobileOpen} onOpenChange={setMobileOpen}><SheetContent side="left" className="p-0 w-[248px]"><SidebarContent onClose={() => setMobileOpen(false)} collapsed={false} /></SheetContent></Sheet>
      <div className="flex-1 flex flex-col min-w-0">
        <header className="h-14 border-b bg-white/60 backdrop-blur flex items-center px-4 lg:px-6 gap-3"><Button variant="ghost" size="icon" className="lg:hidden" onClick={() => setMobileOpen(true)}><Menu className="h-5 w-5" /></Button><div className="flex-1" /></header>
        <main className="flex-1 overflow-auto p-4 sm:p-6 lg:p-8"><Outlet /></main>
      </div>
    </div>
  );
}
